import posthog from "posthog-js";
import type { IUser } from "types/user.types";
import { config } from "utils/config";
import { redactSensitiveUrl } from "utils/redactUrl";

const isEnabled = () => !!config.posthog.apiKey;

export const initPostHog = () => {
	if (!isEnabled()) return;

	posthog.init(config.posthog.apiKey, {
		api_host: config.posthog.apiHost,
		defaults: "2026-01-30",
		person_profiles: "identified_only",
		// OFF. Autocapture sends the TEXT of whatever element was interacted
		// with, and in this app that text is a client's account names, vendor
		// names and amounts. Every funnel event the baseline needs is emitted
		// explicitly instead — see utils/analytics.ts, which now fans the named
		// event catalog out to PostHog as well as Segment/GA.
		autocapture: false,
		capture_pageview: "history_change",
		capture_pageleave: "if_capture_pageview",
		enable_heatmaps: true,
		// 🪤 posthog-js drops a `capture()` SILENTLY when it decides the visitor
		// is a bot — no request, no throw, and no console line, because that
		// branch of `capture()` has no `else`. The predicate is not just the user
		// agent: it ends in `return !!navigator.webdriver`, which every
		// WebDriver-driven browser sets to `true` by spec, and it also matches
		// "HeadlessChrome" in `navigator.userAgentData.brands`. So an automated
		// browser cannot emit a single event, and the symptom is indistinguishable
		// from the whole analytics pipe being dead: `/flags/` still succeeds, a
		// hand-built `fetch()` to `/e/` still returns 200, nothing logs. That cost
		// a full QA cycle on EQU-470, which was filed as a capture outage on dev.
		//
		// Opting out of the filter off prod makes dev browser-verifiable. It is
		// deliberately NOT opted out on prod: there the filter is what keeps
		// crawler traffic out of the funnel.
		//
		// This does not quietly mix bot traffic into the shared project. When the
		// filter is opted out, posthog-js stamps `$browser_type: "bot" | "browser"`
		// on every event, so dev automation stays filterable — and `environment`
		// below already separates dev from prod.
		opt_out_useragent_filter: !config.isProd,
		// Core Web Vitals tracking for SEO and performance monitoring.
		// LCP (Largest Contentful Paint), CLS (Cumulative Layout Shift),
		// FCP (First Contentful Paint), INP (Interaction to Next Paint) are
		// Google ranking factors and page-speed diagnostics.
		capture_performance: {
			web_vitals: true,
			web_vitals_allowed_metrics: ["LCP", "CLS", "FCP", "INP"],
		},
		// 🪤 Session replay is a REMOTE toggle. There is no `replayIntegration()`
		// style opt-in to audit in this bundle: posthog-js starts recording as
		// soon as someone flips "Record user sessions" in the PostHog project
		// settings, with no code change and no deploy. So replay cannot be left
		// to "it is off today" — it is masked here unconditionally, which keeps
		// the capability available to Analytics without the ledger text riding
		// along. `maskTextSelector: "*"` masks every text node; masking applies
		// to children.
		session_recording: {
			maskAllInputs: true,
			maskTextSelector: "*",
		},
		// The marketing site (www.equilityhq.com) and this app (app.equilityhq.com)
		// are different subdomains of one registrable domain. Writing the id cookie
		// on `.equilityhq.com` is what lets a visitor who lands on a marketing page
		// and then arrives at /signup resolve to ONE person instead of two.
		// `localStorage+cookie` persistence (the default) reads localStorage first
		// and falls back to the cookie, which is how the id crosses the hop.
		// Set explicitly rather than left to the library default, because the whole
		// top-of-funnel attribution depends on it.
		cross_subdomain_cookie: true,
		// 🪤 That cookie is shared across `.equilityhq.com`; localStorage is
		// per-origin. In the default `localStorage+cookie` mode a stale
		// localStorage value WINS over a fresher cookie, and `$initial_person_info`
		// — the `{referrer, entry URL}` pair that becomes `$initial_pathname` and
		// `$initial_referrer` — is one of the keys posthog-js copies into the
		// shared cookie (1.417.1: `$device_id`, `distinct_id`, `$sesid`,
		// `$session_is_sampled`, `$epp`, `$initial_person_info`, `$user_state`).
		// So for anyone who had already touched app.equilityhq.com directly, this
		// origin's own `/signin` first touch outranks the `/blog/...` landing the
		// marketing site just wrote, and the post that actually produced the trial
		// never appears. This flag inverts that precedence. www sets the same flag;
		// it is read once at init and is a no-op via `set_config` (EQU-114).
		__preview_cookie_wins_on_conflict: true,
		// A Firebase action code lands in the URL of the reset and verify
		// pages. It is a credential, so it must not be captured as a property.
		// 🪤 `$initial_*` are the ENTRY-URL copies, stored once in persistence and
		// attached to `$set_once` on every subsequent event. Redacting only the
		// live `$current_url` therefore misses the exact case that matters: a user
		// who ARRIVES on the reset link ships the credential with every event for
		// the whole session. `sanitize_properties` does receive the `$set_once`
		// bag, so listing the keys here is sufficient.
		sanitize_properties: (properties) => {
			for (const key of [
				"$current_url",
				"$referrer",
				"$pathname",
				"$initial_current_url",
				"$initial_referrer",
				"$initial_pathname",
			]) {
				if (typeof properties[key] === "string")
					properties[key] = redactSensitiveUrl(properties[key]);
			}
			// `environment` is attached here, per event, and NOT via
			// `posthog.register()`.
			//
			// 🪤 register() writes a super property into persistence, and
			// `posthog.reset()` (logout) wipes persistence. The app is a SPA, so
			// initPostHog() does not run again after a logout — every event for the
			// rest of that page-load then shipped with no environment at all. That
			// is not hypothetical: 42 events on 2026-09-30 and 259 on 2026-09-25
			// arrived untagged, each burst a session that began at /signin
			// immediately after a reset. dev and prod share one PostHog project, so
			// an untagged event is an event nobody can attribute to either.
			//
			// This hook runs on every capture and reads build config, so it cannot
			// be lost by a persistence reset.
			properties.environment = config.env;
			return properties;
		},
	});
};

/**
 * Bind the anonymous device to the real user.
 *
 * The distinct id is the numeric application `user.id` — never the email and
 * never a session id — because that is the only id every other system here
 * keys on. PostHog attaches the current anonymous device id as
 * `$anon_distinct_id` on the resulting `$identify`, which is what merges the
 * pre-signup pageviews into this person.
 *
 * Safe to call more than once per session: PostHog ignores an identify to the
 * distinct id it already holds, so the gate is the caller's convenience, not a
 * correctness requirement.
 */
export const identifyPostHogUser = (user: IUser) => {
	if (!isEnabled()) return;

	posthog.identify(String(user.id), {
		email: user.email,
		firstName: user.firstName,
		lastName: user.lastName,
		name: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || undefined,
		accessLevel: user.accessLevel,
		admin: user.admin === true,
		firebaseUid: user.firebaseUid,
		// So activity rolls up per firm. A person property rather than a PostHog
		// group: group analytics is a paid add-on and this needs no new spend.
		companyId: user.lastCompany?.id,
	});
};

/**
 * Follow the person to the company they are now working in.
 *
 * `identifyPostHogUser` seeds this from `lastCompany` at sign-in; this keeps it
 * honest when the user switches company mid-session, which the firm-level
 * users do constantly.
 */
export const setPostHogCompany = (companyId: number | null) => {
	if (!isEnabled() || companyId === null) return;
	posthog.setPersonProperties({ companyId });
};

export const resetPostHogUser = () => {
	if (!isEnabled()) return;
	posthog.reset();
};

/**
 * The browser's first touch, shaped for the signup request body (EQU-481).
 *
 * ## Why the body and not the cookie
 *
 * `trial_started` is emitted server-side, and the landing page that produced the
 * trial is a fact only the browser holds. The API's intended route for it was the
 * PostHog cookie — shared across `.equilityhq.com`, so a `/blog/...` visit on
 * `www` reaches `app` — read off the request's `Cookie` header. That header never
 * arrives: this app is `app.equilityhq.com`, the API is `api.equilityhq.com`, and
 * the browser omits cookies on a cross-origin request unless both ends opt into
 * credentials. Opting in means an origin allowlist and `Vary: Origin` on every
 * API response for 130 paying customers, which is not a price worth paying for
 * one analytics property — so the same pair travels in the body instead.
 *
 * ## What this returns
 *
 * `$initial_person_info` verbatim: `{ u }` the entry URL and `{ r }` the entry
 * referrer (or posthog-js's `$direct` sentinel). Deliberately NOT the derived
 * `$initial_pathname` — the API sanitises, dropping the query string and
 * fragment, and it must be the only place that rule lives. A Firebase action
 * code can be someone's entry URL, so one sanitiser, server-side, is the whole
 * point.
 *
 * An empty object when PostHog is blocked or has nothing stored. The API then
 * reports no landing page rather than guessing one, because `$set_once` is
 * first-writer-wins and a confident wrong answer is permanent.
 *
 * ## Why only a first touch from ANOTHER host is sent
 *
 * 🪤 `$initial_person_info` is never absent on a live posthog-js. It is written
 * at init, and when nothing is inherited the library SYNTHESISES it from the page
 * being loaded right now — so on a cold `/signup` it holds
 * `{ u: "https://app.equilityhq.com/signup", r: "$direct" }`. Forwarding that
 * verbatim is how the first version of this shipped, and it defeated the whole
 * point twice over: the blog landing never appeared, and a signup with no first
 * touch at all reported `/signup` as its landing page instead of reporting
 * nothing. Both were caught in a browser on dev, not by these tests (EQU-481).
 *
 * Blocking the ingestion endpoint does not help, which is the part that surprises
 * people: an ad blocker stops the network call, and this reads local synchronous
 * persistence, so the ad-blocked signup — the exact visitor this server-side
 * property exists for — shipped the loudest wrong answer.
 *
 * From here, a stored entry URL on THIS host is indistinguishable from one the
 * library just invented, so neither is sent. The value only travels when its host
 * differs from ours, which is precisely the `www` -> `app` hop this exists to
 * carry. The cost is a genuine direct landing on the app itself going unreported;
 * that is a path with no attribution to lose, and `$set_once` being
 * first-writer-wins makes a missing property recoverable where a wrong one is
 * not.
 */
export const postHogFirstTouchFields = (): {
	phInitialEntryUrl?: string;
	phInitialReferrer?: string;
} => {
	if (!isEnabled()) return {};

	try {
		// `get_property` reads posthog-js's own persistence, which is what resolves
		// the shared cookie against this origin's localStorage — including the
		// `__preview_cookie_wins_on_conflict` precedence set at init. Reading the
		// cookie directly here would re-implement that and get it wrong.
		const info = posthog.get_property("$initial_person_info") as
			| { r?: unknown; u?: unknown }
			| undefined;

		if (typeof info?.u !== "string") return {};

		// Not parsed to sanitise — the API is the one sanitiser and the query string
		// is forwarded intact. Parsed only to read the host, which is the one thing
		// that cannot be checked server-side: the API does not know which origin the
		// browser was on.
		let entryHost: string;
		try {
			entryHost = new URL(info.u).hostname;
		} catch {
			// An unparseable entry URL has no provenance to establish, so the pair
			// goes nowhere. The API would reject it anyway.
			return {};
		}

		if (entryHost === window.location.hostname) return {};

		return {
			phInitialEntryUrl: info.u,
			...(typeof info.r === "string" ? { phInitialReferrer: info.r } : {}),
		};
	} catch {
		// Instrumentation does not get to fail a signup.
		return {};
	}
};

export { posthog };
